import { type Duration, intervalToDuration } from 'date-fns';
import { parse as parseDuration, toSeconds } from 'iso8601-duration';

const units = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] as const;

export const getDuration = (startDate: Date, endDate: Date, ignoredUnits: Array<keyof Duration> = []) => {
  const duration = intervalToDuration({
    start: startDate,
    end: endDate,
  });
  return units
    .filter((unit) => duration[unit] && !ignoredUnits.includes(unit))
    .map((unit) => `${duration[unit]}${unit.charAt(0)}`)
    .join(' ');
};

export const getIso8601Duration = (duration: string | undefined) => {
  const parsedDuration = duration && parseDuration(duration);
  return parsedDuration ? getDuration(new Date(0), new Date(toSeconds(parsedDuration) * 1_000), ['seconds']) : '';
};

/**
 * Converts an ISO 8601 duration string to total seconds
 * @param duration ISO 8601 duration string (e.g., 'PT1H30M15S')
 * @returns number of seconds
 */
const iso8601ToSeconds = (duration: string): number => {
  const matches = duration.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!matches) {
    return 0;
  }

  const [, hours, minutes, seconds] = matches;
  return parseInt(hours || '0') * 3600 + parseInt(minutes || '0') * 60 + parseInt(seconds || '0');
};

/**
 * Converts seconds to ISO 8601 duration format
 * @param totalSeconds number of seconds
 * @returns ISO 8601 duration string
 */
const secondsToIso8601 = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let result = 'PT';
  if (hours > 0) {
    result += `${hours}H`;
  }
  if (minutes > 0) {
    result += `${minutes}M`;
  }
  if (seconds > 0 || (!hours && !minutes)) {
    result += `${seconds}S`;
  }

  return result;
};

/**
 * Sums up an array of ISO 8601 duration strings
 * @param durations Array of ISO 8601 duration strings (e.g., ['PT1H30M', 'PT45M15S'])
 * @returns Total duration in ISO 8601 format
 */
export const sumIso8601Durations = (durations: string[]): string => {
  const totalSeconds = durations.reduce((sum, duration) => sum + iso8601ToSeconds(duration), 0);

  return secondsToIso8601(totalSeconds);
};

export const iso8601ToMilliseconds = (duration: string | undefined): number => {
  if (!duration) {
    return 0;
  }

  try {
    const parsed = parseDuration(duration);
    return toSeconds(parsed) * 1000;
  } catch (error) {
    console.error('Invalid ISO 8601 duration format:', duration, error);
    return 0;
  }
};
